/**
 * Step validation configuration for RMC order intake stepper
 * Centralizes all validation logic for better maintainability
 */

import {FieldsForm} from '@hconnect/tuc-processes/fields/types'

import {RmcOrderIntakeStepType} from '../config/stepTypes'

/**
 * Configuration for step-specific validation fields
 * Maps each step type to the form fields that need validation
 */
export const STEP_VALIDATION_CONFIG = {
  [RmcOrderIntakeStepType.QUOTE]: ['contract'], // Quote selection step
  [RmcOrderIntakeStepType.MATERIAL]: ['material'], // Material step
  [RmcOrderIntakeStepType.QUANTITY]: ['quantity', 'plusLoadExpected', 'truckType'], // Quantity step
  [RmcOrderIntakeStepType.USAGE]: ['concreteUsage'], // Usage step
  [RmcOrderIntakeStepType.UNLOADING_METHOD]: [
    'unloadingMethod',
    'unloadingMethod.unloadingMethodCode',
    'unloadingMethod.pump'
  ], // Unloading step
  [RmcOrderIntakeStepType.INTERVALS]: ['loadSpacing', 'timeOnJobSite'], // Spacing step
  [RmcOrderIntakeStepType.DATE]: ['dateTime'], // Date step
  [RmcOrderIntakeStepType.TIME]: ['dateTime'], // Time step
  [RmcOrderIntakeStepType.DETAILS]: [
    'additionalNotes',
    'poNumber',
    'driverInstructions',
    'orderPlacer.name',
    'orderPlacer.phoneNumber',
    'siteContact.name',
    'siteContact.phoneNumber'
  ] // Details step with contact fields
} as const

/**
 * Type for step validation configuration keys
 */
export type StepType = keyof typeof STEP_VALIDATION_CONFIG

/**
 * Map from numeric step index to step type
 * Only used for backward compatibility during refactoring
 */
export const STEP_INDEX_TO_TYPE_MAP: Record<number, RmcOrderIntakeStepType> = {
  0: RmcOrderIntakeStepType.QUOTE,
  1: RmcOrderIntakeStepType.MATERIAL,
  2: RmcOrderIntakeStepType.QUANTITY,
  3: RmcOrderIntakeStepType.USAGE,
  4: RmcOrderIntakeStepType.UNLOADING_METHOD,
  5: RmcOrderIntakeStepType.INTERVALS,
  6: RmcOrderIntakeStepType.DATE,
  7: RmcOrderIntakeStepType.TIME,
  8: RmcOrderIntakeStepType.DETAILS
}

/**
 * Get validation field names for a specific step
 */
export const getStepValidationFields = (
  stepTypeOrIndex: RmcOrderIntakeStepType | number
): string[] => {
  // Handle backward compatibility with index-based calls
  const stepType =
    typeof stepTypeOrIndex === 'number' ? STEP_INDEX_TO_TYPE_MAP[stepTypeOrIndex] : stepTypeOrIndex

  const fields = (stepType && STEP_VALIDATION_CONFIG[stepType]) || []
  return [...fields] // Convert readonly array to mutable array
}

/**
 * Check if a step has validation requirements
 */
export const hasStepValidation = (stepTypeOrIndex: RmcOrderIntakeStepType | number): boolean => {
  // Handle backward compatibility with index-based calls
  const stepType =
    typeof stepTypeOrIndex === 'number' ? STEP_INDEX_TO_TYPE_MAP[stepTypeOrIndex] : stepTypeOrIndex

  return !!stepType && stepType in STEP_VALIDATION_CONFIG
}

/**
 * Validation function type for React Hook Form trigger
 */
export type ValidationTrigger = (name?: string | string[]) => Promise<boolean>

/**
 * Step-specific validation logic
 */
export const validateStep = async (
  stepTypeOrIndex: RmcOrderIntakeStepType | number,
  trigger: ValidationTrigger
): Promise<boolean> => {
  const fields = getStepValidationFields(stepTypeOrIndex)

  if (fields.length === 0) {
    // No validation required for this step
    return true
  }

  if (fields.length === 1) {
    // Single field validation
    return await trigger(fields[0])
  }

  // Multiple field validation - all must be valid
  const validationResults = await Promise.all(fields.map((field) => trigger(field)))

  return validationResults.every(Boolean)
}

/**
 * Check if all required fields for a step are present in form data
 */
export const hasRequiredStepData = (
  stepTypeOrIndex: RmcOrderIntakeStepType | number,
  formData: Partial<FieldsForm>
): boolean => {
  const fields = getStepValidationFields(stepTypeOrIndex)

  return fields.every((fieldPath) => {
    // Handle nested field paths like 'quantity.volume'
    const value = getNestedValue(formData, fieldPath)
    return value !== undefined && value !== null && value !== ''
  })
}

/**
 * Helper to get nested object values using dot notation
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  const parts = path.split('.')
  let current: unknown = obj

  for (const part of parts) {
    if (current === undefined || current === null) {
      return undefined
    }
    if (typeof current === 'object' && current !== null) {
      current = (current as Record<string, unknown>)[part]
    } else {
      return undefined
    }
  }

  return current
}
